import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { MonthlySalesPDF } from '@/components/layouts/Ir/Pdffiles'

const TopicsCocotoraPage = () => {
  return (
    <>
      <CompanyMain />
    </>
  )
}

export default TopicsCocotoraPage

TopicsCocotoraPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '個人投資家の皆様へ', path: '/ir/investor/' },
    { name: 'ココ見てトライアル', path: '' }
  ];

  const yearsPdf = [
    {
      accordionTitle: 'ココ見てトライアル アーカイブ',
      accordionItems: [{
        id: '',
        fileName: '2025年6月期 ココ見てトライアル',
        filePath: '/retail-investor/topics-cocotora2025/'
      },{
        id: '',
        fileName: '2024年６月期 TRIALレポート',
        filePath: 'https://report-k.pdcp.jp/141A/202409/a/'
      }],
      isDefaultOpen: true
    }
  ];

  return (
    <>
      <Head
        title="企業情報"
        description="トライアルホールディングスの企業情報を掲載しています。"
        ogp={{ url: 'company/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        <TopicsSection>
          <TopicsTitleBuild>
            中期経営計画<TopicsTitleBuildDate>(2027年６月期～2029年６月期)</TopicsTitleBuildDate>の１年目が始まりました！
          </TopicsTitleBuild>

          <IntroText>
            今回の「ココ見てトライアル」では、中期経営計画５つの重点戦略のうち、<br className="pc-only" />
            トライアル×西友の融合を象徴する新フォーマット「トライアル西友」を徹底解説します！
          </IntroText>

          <ScrollableImageWrapper>
            <ScrollableImage src="/images/topics_cocotora64.png" alt="トライアル西友 徹底解説" />
          </ScrollableImageWrapper>

          <SectionTitle>
            <span>●</span> 「トライアル西友」誕生
          </SectionTitle>
          <BirthFlex>
            <BirthImage src="/images/topics_cocotora42.png" alt="トライアル西友" />
            <BirthText>
              「GMS再生」モデルづくりを目指した
              <br className="sp-only" />
              「都市型の新フォーマット」
            </BirthText>
          </BirthFlex>
          <SectionText>
            駅前・徒歩商圏で繁盛店を実現するため、「食」の強化を軸に、利便性の向上と非食品の充実を図る。それが「トライアル西友」のコンセプトです。西友が培ってきた、駅前好立地を活かした地域の生活需要を捉える店舗基盤。そこに、「Skip Cart」や「インストアサイネージ」をはじめとするトライアルのリテールテックと、店舗運営のノウハウを融合。両社の強みを掛け合わせた新フォーマット「トライアル西友」が誕生しました。
          </SectionText>

          <ScrollableImageWrapper>
            <ScrollableImage src="/images/topics_cocotora43.png" alt="トライアル西友 詳細" />
          </ScrollableImageWrapper>

          <SectionTitleFlex>
            <SectionTitleText>
              <span>●</span> 「トライアル西友」フォーマット責任者インタビュー
            </SectionTitleText>
            <SectionTitleImage src="/images/topics_cocotora57.png" alt="インタビュー" />
          </SectionTitleFlex>

          <QASection>
            <QABlock>
              <QARow>
                <QIcon>Q</QIcon>
                <QText>今回の新フォーマットでは、従来のお店と比べて何を最も大きく変えたのでしょうか？</QText>
              </QARow>
              <QARow>
                <AIcon>A</AIcon>
                <AText>
                  一番分かりやすい変化は、生鮮食品の強化です。生鮮は大きな集客ドライバーであり、競争力を高めるうえで欠かせないカテゴリーだと考えています。
                  一方で、生鮮だけでなく非食品も幅広く揃え、日常生活に必要なものを一つの店舗で購入できる「ワンストップショッピング」を目指しています。差別化できる強いカテゴリーをつくり、<HighlightText>「欲しいものがある」「おいしいものがある」「便利に買える」</HighlightText>と感じていただける店舗を目指しています。
                </AText>
              </QARow>
            </QABlock>

            <QABlock>
              <QARow>
                <QIcon>Q</QIcon>
                <QText>これまでの店舗づくりで得た知見を、今後どのように新しい店舗へ生かしていきたいですか？</QText>
              </QARow>
              <QARow>
                <AIcon>A</AIcon>
                <AText>
                  これまで、駅前型や都心型、多層階型など、さまざまな店舗を運営する中で、それぞれの商圏や店舗特性に合った知見を蓄積してきました。今後は、<HighlightText>成功事例を「武器」として増やし、店舗ごとに最適に組み合わせながら、競争力を高めていきたい</HighlightText>と考えています。
                </AText>
              </QARow>
            </QABlock>

            <QABlock>
              <QARow>
                <QIcon>Q</QIcon>
                <QText>実際の運営を通じて、新たに見えてきた課題はありますか？</QText>
              </QARow>
              <QARow>
                <AIcon>A</AIcon>
                <AText>
                  実際に運営する中で見えてきた課題は、成功事例をそのまま全店舗に横展開できるわけではないという点です。店舗ごとに商圏や客層、競争環境、店舗特性が異なるため、品揃えや価格、売場をそれぞれに合わせて調整する必要があります。また、改装して終わりではなく、新たに成果が出た施策を他店舗にも取り入れながら、<HighlightText>継続的に店舗をアップデートしていく</HighlightText>ことも重要だと考えています。
                </AText>
              </QARow>
            </QABlock>
          </QASection>

          <BottomFlex>
            <PersonLeft>
              <PersonTopText>＼ この方に聞きました ／</PersonTopText>
              <PersonImage src="/images/topics_cocotora44.png" alt="出口 直樹" />
              <PersonInfo>
                <p>トライアル西友</p>
                <p>フォーマット責任者</p>
                <p>出口 直樹</p>
              </PersonInfo>
            </PersonLeft>

            <MessageRight>
              <MessageBox>
                <MessageTitleWrapper>
                  <MessageTitle>株主・投資家の皆さまへ</MessageTitle>
                </MessageTitleWrapper>
                <MessageContent>
                  日本の小売業界には、強い商品力を持つ魅力的な企業が数多く存在します。<br />
                  その中でトライアル西友を選んでいただけるよう差別化を図りながら、<br className="pc-only" />
                  店舗の魅力をさらに高めていきたいと考えています。まだ完成形ではありませんが、<br className="pc-only" />
                  一つひとつ強みを積み上げて、競争力のあるモデルへ磨き上げていきます。<br />
                  これからも、より良い店舗づくりへの挑戦を続けていきます。
                </MessageContent>
              </MessageBox>
            </MessageRight>
          </BottomFlex>

          <SectionTitle>
            <span>●</span> 「トライアル西友」の店内をのぞいてみよう
          </SectionTitle>
          <SectionText>
            トライアル西友では、トライアルのリテールテックや生鮮・惣菜のノウハウと、西友の価値訴求のPB商品「みなさまのお墨付き」をはじめとする商品力など、両社の強みを融合。従来の売場から、より商品を選びやすく、その魅力が伝わる売場へ。生鮮の鮮度・品揃えの強化に加え、お酒やビューティーなどのカテゴリーでも、お客様に「この店に来たい」と思っていただける売場づくりを進めています。
          </SectionText>

          <InsideGrid>
            <InsideReplaceImage
              src="/images/topics_cocotora65.png"
              alt="「Skip Cart」でスマートで便利なお買い物体験"
              width={500}
              height={190}
              style={{ maxWidth: '500px' }}
            />
            <InsideReplaceImage
              src="/images/topics_cocotora67.png"
              alt="「インストアサイネージ」で臨場感のある楽しい売場"
              width={500}
              height={235}
              style={{ maxWidth: '500px' }}
            />
            <InsideReplaceImage
              src="/images/topics_cocotora66.png"
              alt="生鮮を中心とする食品強化で「商品力アップ」"
              width={500}
              height={234}
              style={{ maxWidth: '500px' }}
            />
            <InsideReplaceImage
              src="/images/topics_cocotora68.png"
              alt="「九州愛」あふれる商品まで豊富に取り扱い"
              width={500}
              height={182}
              style={{ maxWidth: '500px' }}
            />
          </InsideGrid>

          <SectionTitleFlex>
            <SectionTitleText>
              <span>●</span> トライアル西友 店長インタビュー
            </SectionTitleText>
            <SectionTitleImage src="/images/topics_cocotora57.png" alt="インタビュー" />
          </SectionTitleFlex>

          <QASection>
            <QABlock>
              <QARow>
                <QIcon>Q</QIcon>
                <QText>業態転換の前後でどのような変化がありますか？</QText>
              </QARow>
              <QARow>
                <AIcon>A</AIcon>
                <AText>
                  従来のお客さまに加え、ファミリー層や若い世代にも利用いただけるようになり、<HighlightText>お客さま層の広がりを感じています</HighlightText>。<br />
                  西友が持つ店舗や人材のポテンシャルに、トライアルのノウハウや新たな取り組みを掛け合わせることで、店舗の魅力をさらに引き出していけると考えています。
                </AText>
              </QARow>
            </QABlock>

            <QABlock>
              <QARow>
                <QIcon>Q</QIcon>
                <QText>「トライアル西友」として運営してきて、特に注目の売場はどこですか？</QText>
              </QARow>
              <QARow>
                <AIcon>A</AIcon>
                <AText>
                  やはり、<HighlightText>お肉や惣菜をはじめとする生鮮食品です</HighlightText>。実際にお客さまから支持していただいているという手応えを感じています。<br />
                  日常のお買い物で「まず選ばれる店」になるためには、生鮮の力が非常に重要です。また、九州など西日本で馴染みのある商品を東京でも購入できることも、トライアル西友ならではの魅力だと考えています。東京ではなかなか見かけない商品もあるため、そうした品揃えが新たな来店理由にもつながっています。
                </AText>
              </QARow>
            </QABlock>

            <LastQARow>
              <LastQAColumn>
                <QARow>
                  <QIcon>Q</QIcon>
                  <QText>今後、さらに良くしていきたい点や、新しく挑戦したいことはありますか？</QText>
                </QARow>
                <QARow>
                  <AIcon>A</AIcon>
                  <AText>
                    現在は、まだ十分に育ち切っていないカテゴリーや売場もあります。一方で、他のトライアル西友店舗では、すでに成果が出ている取り組みもあります。トライアルでは、各店舗や各担当者の取り組み、成功事例などを共有する仕組みがあります。<br />
                    今後は、そうした成功事例を自店にも素早く取り入れ、継続的にアップデートしていくことが重要だと考えていますので、<HighlightText>新しい取り組みを柔軟に受け入れ、実行できる店舗づくりに挑戦していきたいです</HighlightText>。
                  </AText>
                </QARow>
              </LastQAColumn>
              <LastQAImage src="/images/topics_cocotora62.png" alt="店長インタビュー" />
            </LastQARow>
          </QASection>

          <BottomFlex>
            <PersonLeft>
              <PersonTopText>＼ この方に聞きました ／</PersonTopText>
              <PersonImageSmall src="/images/topics_cocotora63.png" alt="鳥谷部 和世" />
              <PersonInfo>
                <p>トライアル西友 花小金井店長</p>
                <p>鳥谷部 和世</p>
              </PersonInfo>
            </PersonLeft>

            <MessageRight>
              <MessageBox>
                <MessageTitleWrapper>
                  <MessageTitle>株主・投資家の皆さまへ</MessageTitle>
                </MessageTitleWrapper>
                <MessageContent>
                  「トライアル」という社名そのものが、私たちらしさを表していると思います。<br />
                  私たちは、失敗しても諦めず、リカバリーしながら最後までやり切る会社です。<br />
                  これまで西友では、やりたくても諦めざるを得なかったこともありました。<br />
                  トライアルグループ入りを機に、そこからさらに大きな挑戦ができる環境になっています。これからも当社グループのトライし続ける姿に期待し、応援していただければうれしいです。
                </MessageContent>
              </MessageBox>
            </MessageRight>
          </BottomFlex>

          <SectionTitle>
            <span>●</span> Webマガジン{'　'}くらしトライのご紹介
          </SectionTitle>
          <SectionText>
            「くらしトライ」では、おすすめ商品や暮らしに役立つ情報を日々発信しています。<br />
            毎日の食卓がもっと楽しくなるアイデアや、商品の裏側にあるストーリーをお届けします。
          </SectionText>

          <MagazineGrid>
            <MagazineItem href="https://www.trial-net.co.jp/mag/detail/30730/?utm_source=IR&utm_medium=tsushin&utm_campaign=TRIALMAGAZINE&utm_content=shioya/mizu" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora45.png" alt="天然水" />
            </MagazineItem>
            <MagazineItem href="https://www.trial-net.co.jp/mag/detail/24474/?utm_source=IR&utm_medium=tsushin&utm_campaign=TRIALMAGAZINE&utm_content=nabetsuyu" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora46.png" alt="鍋つゆ" />
            </MagazineItem>
            <MagazineItem href="https://www.trial-net.co.jp/mag/detail/30565/?utm_source=IR&utm_medium=tsushin&utm_campaign=TRIALMAGAZINE&utm_content=toripaitan" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora47.png" alt="鶏白湯" />
            </MagazineItem>
            <MagazineItem href="https://www.trial-net.co.jp/mag/detail/23626/?utm_source=IR&utm_medium=tsushin&utm_campaign=TRIALMAGAZINE&utm_content=trial/plus" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora48.png" alt="トライアル＋" />
            </MagazineItem>
          </MagazineGrid>

          <DownArrowSection>
            <DownArrowRow>
              <DownArrow />
              <DownArrowText>こちらからもご覧いただけます</DownArrowText>
            </DownArrowRow>
            <DownArrowImageLink href="https://www.trial-net.co.jp/mag/?utm_source=IR&utm_medium=tsushin&utm_campaign=TRIALMAGAZINE&utm_content=TRIALMAGAZINE" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora_31.png" alt="くらしトライ" />
            </DownArrowImageLink>
          </DownArrowSection>

          <SectionTitle>
            <span>●</span> 店舗検索サイト・公式Instagramのご紹介
          </SectionTitle>
          <SectionText>
            お近くの店舗の検索や、最新のお得な情報・キャンペーンなどをご確認いただけます。
          </SectionText>

          <StoreLinksFlex>
            <StoreLinksColumn>
              <StoreMainImage src="/images/topics_cocotora49.png" alt="トライアル" />
              <StoreSubFlex>
                <StoreSubColumn>
                  <StoreSubItem>
                    <StoreSubIcon src="/images/topics_cocotora50.png" alt="店舗検索" />
                    <StoreLink href="https://www.trial-net.co.jp/shops" target="_blank" rel="noopener noreferrer">
                      トライアルの店舗を探す
                    </StoreLink>
                  </StoreSubItem>
                  <StoreSubItem>
                    <StoreSubIconLast src="/images/topics_cocotora51.png" alt="Instagram" />
                    <StoreLink href="https://www.instagram.com/trialcompany_official?stkn=cmp3Y2U5czRpcHgw&utm_source=qr?utm_source=HP&utm_medium=Instagram&utm_campaign=Instagram&utm_content=profile" target="_blank" rel="noopener noreferrer">
                      トライアル公式Instagram
                    </StoreLink>
                  </StoreSubItem>
                </StoreSubColumn>
                <StoreSubRight>
                  <StoreSubRightImage src="/images/topics_cocotora52.png" alt="trialcompany_official" />
                  <StoreSubRightText>trialcompany_official</StoreSubRightText>
                </StoreSubRight>
              </StoreSubFlex>
            </StoreLinksColumn>

            <StoreLinksColumn>
              <StoreMainImage src="/images/topics_cocotora_40.png" alt="西友" />
              <StoreSubFlex>
                <StoreSubColumn>
                  <StoreSubItem>
                    <StoreSubIcon src="/images/topics_cocotora50.png" alt="店舗検索" />
                    <StoreLink href="https://www.seiyu.co.jp/searchshop/" target="_blank" rel="noopener noreferrer">
                      西友の店舗を探す
                    </StoreLink>
                  </StoreSubItem>
                  <StoreSubItem>
                    <StoreSubIconLast src="/images/topics_cocotora51.png" alt="Instagram" />
                    <StoreLink href="https://www.instagram.com/seiyu_official/" target="_blank" rel="noopener noreferrer">
                      西友公式Instagram
                    </StoreLink>
                  </StoreSubItem>
                </StoreSubColumn>
                <StoreSubRight>
                  <StoreSubRightImage src="/images/topics_cocotora53.png" alt="seiyu_official" />
                  <StoreSubRightText>seiyu_official</StoreSubRightText>
                </StoreSubRight>
              </StoreSubFlex>
            </StoreLinksColumn>
          </StoreLinksFlex>

          <VideoLinksFlex>
            <VideoLink href="https://youtu.be/xykrw7-i4mQ?si=kkx3JNJWemUW63x3" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora54.png" alt="HD紹介" />
              <VideoLinkText>トライアルホールディングス紹介動画</VideoLinkText>
            </VideoLink>
            <VideoLink href="https://youtu.be/LAGfCH5c2yg?si=4j6-mXOK0az7hrLl" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora55.png" alt="流通情報革命" />
              <VideoLinkText>Retail AI 流通情報革命</VideoLinkText>
            </VideoLink>
            <VideoLink href="https://youtu.be/-I48rKRbsvw?si=JSvvLld6si1czUYd" target="_blank" rel="noopener noreferrer">
              <img src="/images/topics_cocotora56.png" alt="宮若" />
              <VideoLinkText>福岡県宮若市で、リテールDX推進</VideoLinkText>
            </VideoLink>
          </VideoLinksFlex>

          <MonthlySalesPDF yearsPdf={yearsPdf} />
        </TopicsSection>
      </TitleLayout>
    </>
  )
}

const CompanyMain = styled.div`
  width: 1040px;
  margin: 0 auto;
  padding: 98px 0 130px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 40px 20px 70px;
  }
`

const TopicsSection = styled.div`
  width: 1040px;
  margin: 0 auto;
  padding: 30px 0 130px;
  img {
    width: 100%;
  }
  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 40px 20px 70px;
  }
`

const TopicsTitleBuild = styled.div`
  width: 100%;
  padding: 15px 10px;
  font-size: 32px;
  font-weight: bold;
  color: #FFF;
  margin-top: 50px;
  background: ${theme.colors.trialBlue};
  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 26px;
    margin-top: 30px;
  }
`

const TopicsTitleBuildDate = styled.span`
  font-size: 18px;
  font-weight: bold;
  color: #FFF;
  margin-left: 10px;

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    margin-left: 5px;
  }
`

const IntroText = styled.div`
  margin-top: 40px;
  margin-bottom: 50px;
  text-align: center;
  font-size: 22px;
  font-weight: bold;
  color: #2F5597;
  line-height: 36px;

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 24px;
    margin-top: 20px;
    margin-bottom: 30px;
    .pc-only {
      display: none;
    }
  }
`

const SectionTitle = styled.div`
  margin-top: 60px;
  font-size: 26px;
  font-weight: bold;
  color: #333;
  line-height: 34px;

  span {
    margin-right: 8px;
  }

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 26px;
    margin-top: 40px;
  }
`

const SectionTitleFlex = styled.div`
  margin-top: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  @media screen and (max-width: 1047px) {
    margin-top: 40px;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`

const SectionTitleText = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: #333;
  line-height: 34px;
  flex: 1;

  span {
    margin-right: 8px;
  }

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 26px;
  }
`

const SectionTitleImage = styled.img`
  width: 290px !important;
  height: 100px;
  object-fit: contain;
  flex-shrink: 0;

  @media screen and (max-width: 1047px) {
    width: 200px !important;
    height: 70px;
    align-self: flex-end;
  }
`

const BirthFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  margin-top: 20px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 15px;
  }
`

const BirthImage = styled.img`
  width: 218px !important;
  height: 50px;
  object-fit: contain;
  flex-shrink: 0;

  @media screen and (max-width: 1047px) {
    width: 180px !important;
    height: 42px;
    align-self: flex-start;
  }
`

const BirthText = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #2F5597;
  line-height: 36px;

  .sp-only {
    display: none;
  }

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 28px;

    .sp-only {
      display: inline;
    }
  }
`

const SectionText = styled.p`
  font-size: 18px;
  line-height: 32px;
  color: #333;
  margin-top: 20px;

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 26px;
  }
`

const ScrollableImageWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 30px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: 1047px) {
    overflow-x: visible;
  }
`

const ScrollableImage = styled.img`
  display: block;
  min-width: 1040px;
  width: 100%;
  max-width: none;

  @media screen and (max-width: 1047px) {
    min-width: 0;
    width: 100%;
  }
`

const QASection = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 50px;

  @media screen and (max-width: 1047px) {
    margin-top: 40px;
    gap: 30px;
  }
`

const QABlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media screen and (max-width: 1047px) {
    gap: 15px;
  }
`

const QARow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;

  @media screen and (max-width: 1047px) {
    gap: 10px;
  }
`

const QIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #002D65;
  color: #FFF;
  font-weight: bold;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;

  @media screen and (max-width: 1047px) {
    width: 28px;
    height: 28px;
    font-size: 16px;
  }
`

const AIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #C2CDDA;
  color: #002D65;
  font-weight: bold;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;

  @media screen and (max-width: 1047px) {
    width: 28px;
    height: 28px;
    font-size: 16px;
  }
`

const QText = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  line-height: 30px;
  padding-top: 3px;

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 24px;
  }
`

const AText = styled.div`
  font-size: 16px;
  font-weight: normal;
  color: #333333;
  line-height: 28px;
  padding-top: 3px;

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 24px;
  }
`

const HighlightText = styled.span`
  color: #002D65;
  font-weight: bold;
`

const LastQARow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 20px;
  }
`

const LastQAColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media screen and (max-width: 1047px) {
    gap: 15px;
  }
`

const LastQAImage = styled.img`
  width: 320px !important;
  height: 170px;
  object-fit: contain;
  flex-shrink: 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    max-width: 320px;
    height: auto;
    align-self: center;
  }
`

const BottomFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  margin-top: 60px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 30px;
    margin-top: 40px;
  }
`

const PersonLeft = styled.div`
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const PersonTopText = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;

  @media screen and (max-width: 1047px) {
    font-size: 16px;
  }
`

const PersonImage = styled.img`
  width: 235px !important;
  height: 170px;
  object-fit: cover;
  margin-bottom: 15px;

  @media screen and (max-width: 1047px) {
    width: 200px !important;
    height: 145px;
  }
`

const PersonImageSmall = styled.img`
  width: 145px !important;
  height: 150px;
  object-fit: cover;
  margin-bottom: 15px;

  @media screen and (max-width: 1047px) {
    width: 120px !important;
    height: 125px;
  }
`

const PersonInfo = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  line-height: 30px;

  p {
    margin: 0;
  }

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 26px;
  }
`

const MessageRight = styled.div`
  width: 65%;
  position: relative;
  padding-top: 20px;

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const MessageBox = styled.div`
  border: 2px dashed #999;
  padding: 40px 30px 30px;
  position: relative;

  @media screen and (max-width: 1047px) {
    padding: 30px 15px 20px;
  }
`

const MessageTitleWrapper = styled.div`
  position: absolute;
  top: -14px;
  left: 20px;
  background: #FFF;
  padding: 0 12px;
`

const MessageTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;

  @media screen and (max-width: 1047px) {
    font-size: 16px;
  }
`

const MessageContent = styled.div`
  font-size: 16px;
  line-height: 30px;
  color: #333;

  @media screen and (max-width: 1047px) {
    font-size: 13px;
    line-height: 24px;

    .pc-only {
      display: none;
    }
  }
`

const InsideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-top: 30px;

  & > *:nth-child(1) {
    order: 1;
  }
  & > *:nth-child(2) {
    order: 3;
  }
  & > *:nth-child(3) {
    order: 2;
  }
  & > *:nth-child(4) {
    order: 4;
  }

  @media screen and (max-width: 1047px) {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 20px;

    & > *:nth-child(1) {
      order: 1;
    }
    & > *:nth-child(2) {
      order: 2;
    }
    & > *:nth-child(3) {
      order: 3;
    }
    & > *:nth-child(4) {
      order: 4;
    }
  }
`

const InsideReplaceImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const MagazineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 30px;

  @media screen and (max-width: 1047px) {
    gap: 15px;
    margin-top: 20px;
  }
`

const MagazineItem = styled.a`
  display: block;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`

const DownArrowSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 50px;

  @media screen and (max-width: 1047px) {
    margin-top: 30px;
  }
`

const DownArrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 15px;
`

const DownArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-top: 20px solid #333;
  flex-shrink: 0;

  @media screen and (max-width: 1047px) {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 14px solid #333;
  }
`

const DownArrowText = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #333;

  @media screen and (max-width: 1047px) {
    font-size: 15px;
  }
`

const DownArrowImageLink = styled.a`
  display: block;
  max-width: 400px;
  width: 100%;

  img {
    width: 100%;
    height: auto;
  }

  @media screen and (max-width: 1047px) {
    width: 50%;
    margin: 0 auto;
  }
`

const StoreLinksFlex = styled.div`
  display: flex;
  gap: 50px;
  margin-top: 30px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 30px;
  }
`

const StoreLinksColumn = styled.div`
  flex: 1;
`

const StoreMainImage = styled.img`
  width: 180px !important;
  object-fit: contain;
  display: block;
  margin-bottom: 20px;
  margin-top: 20px;

  @media screen and (max-width: 1047px) {
    width: 140px !important;
    height: 18px;
  }
`

const StoreSubFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-left: 30px;
`

const StoreSubColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
`

const StoreSubItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const StoreSubIcon = styled.img`
  width: 50px !important;
  height: 50px;
  object-fit: contain;
  flex-shrink: 0;
`

const StoreSubIconLast = styled.img`
  width: 45px !important;
  height: 45px;
  object-fit: contain;
  flex-shrink: 0;
`

const StoreLink = styled.a`
  font-size: 18px;
  color: #333;
  text-decoration: underline;
  font-weight: bold;

  &:hover {
    opacity: 0.7;
    color: #3786C7;
  }

  @media screen and (max-width: 1047px) {
    font-size: 14px;
  }
`

const StoreSubRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  @media screen and (max-width: 1047px) {
    width: 140px;
  }
`

const StoreSubRightImage = styled.img`
  width: 100px !important;
  height: 100px;
  object-fit: cover;
  display: block;

  @media screen and (max-width: 1047px) {
    width: 70px !important;
    height: 70px;
  }
`

const StoreSubRightText = styled.div`
  font-size: 14px;
  color: #333;
  margin-top: 8px;
  text-align: center;

  @media screen and (max-width: 1047px) {
    font-size: 12px;
  }
`

const VideoLinksFlex = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 60px;
  margin-bottom: 60px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 20px;
    margin-top: 40px;
  }
`

const VideoLink = styled.a`
  flex: 1;
  display: block;
  transition: opacity 0.3s;
  text-align: center;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`

const VideoLinkText = styled.div`
  margin-top: 12px;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  line-height: 26px;

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 22px;
    margin-top: 8px;
  }
`